import { isBoolean, isFunction, isList, isNumber, isString } from './guards';
import type {
  ElementRef,
  AttrValue,
  Child,
  DomPropertyValue,
  DynAttr,
  Dynamic,
  EventBinding,
  EventHandler,
  Gate,
  PropInput,
  Props,
  Reader,
  SigValue,
} from './types';
import {
  InstructionError,
  isDynamic,
  isInstruction,
  isSignalLike,
  toReader,
} from './values';

/** The values `attrString` writes; everything else is a contract violation. */
type AttrInput = string | number | bigint | boolean | null | undefined;

function isAttrInput(value: PropInput): value is AttrInput {
  return (
    value === null ||
    value === undefined ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'bigint' ||
    typeof value === 'boolean'
  );
}

export interface AppliedProps {
  attrs: Record<string, string>;
  dyn: DynAttr[];
  on: EventBinding[];
  gate?: Gate;
  key?: string;
  ref?: ElementRef;
}

/** Parses `"action subject"` or `"action subject.field"`. */
export function parseGate(spec: string): Gate {
  const parts = spec.trim().split(/\s+/);
  const [action, target] = parts;
  if (parts.length !== 2 || !action || !target) {
    throw new InstructionError(
      `Gate must be "action subject[.field]", got "${spec}"`,
    );
  }
  const dot = target.indexOf('.');
  if (dot === -1) return { action, subject: target };
  const subject = target.slice(0, dot);
  const field = target.slice(dot + 1);
  if (!subject || !field) {
    throw new InstructionError(
      `Gate must be "action subject[.field]", got "${spec}"`,
    );
  }
  return { action, subject, field };
}

/**
 * The attribute text a value writes for the attribute `name`, or `null` when
 * the attribute is absent. An ARIA state is a token, not an HTML boolean
 * attribute: `aria-expanded={false}` writes "false" rather than nothing.
 */
export function attrText(name: string, value: PropInput): string | null {
  if (isBoolean(value) && name.startsWith('aria-'))
    return value ? 'true' : 'false';
  return attrString(value);
}

/** The attribute text a value writes, or `null` when the attribute is absent. */
export function attrString(value: PropInput): string | null {
  if (!isAttrInput(value)) {
    throw new InstructionError(
      'Attribute values must be strings, numbers or booleans',
    );
  }
  if (value === null || value === undefined || value === false) return null;
  if (value === true) return '';
  return isString(value) ? value : String(value);
}

function readerOf<T>(
  value: PropInput,
  convert: (raw: DomPropertyValue) => T,
): Reader<T> {
  if (isDynamic(value)) {
    // SAFETY: a prop's reader yields a bindable value, or a reader of one, which the loop below reads through.
    const read = toReader(value as Dynamic<BindableValue>);
    return () => {
      // A reader that yields a reader is read through: the compiler wraps every
      // prop expression, so `prop:value={value}` with `value` already a reader
      // arrives as a reader of that reader.
      let current: BindableValue | Dynamic<BindableValue> = read();
      while (isDynamic(current)) current = toReader<BindableValue>(current)();
      return convert(propertyValueOf(current));
    };
  }
  if (isFunction(value)) {
    throw new InstructionError(
      'A binding received a function with parameters; use a zero-argument reader',
    );
  }
  return () => convert(propertyValueOf(value));
}

type PlainPropInput = Exclude<
  PropInput,
  Dynamic<DomPropertyValue> | EventHandler
>;

/** What a binding's reader may yield; instructions belong in a child position, and are refused here. */
type BindableValue = DomPropertyValue | SigValue;

/** A plain prop value is a DOM property value; instructions and readers never reach a binding, nested signals are read by the consumer. */
function propertyValueOf(
  value: PlainPropInput | Child | BindableValue,
): DomPropertyValue {
  if (isInstruction(value)) {
    throw new InstructionError(
      'An instruction cannot be bound to an attribute or property',
    );
  }
  if (isSignalLike(value)) return { get: () => propertyValueOf(value.get()) };
  if (isFunction(value)) {
    throw new InstructionError(
      'A reader inside a list cannot be bound; bind the list itself as a reader',
    );
  }
  if (isList(value)) return value.map(propertyValueOf);
  return value;
}

function attrValueOf(raw: DomPropertyValue): AttrValue {
  if (
    raw === null ||
    raw === undefined ||
    isString(raw) ||
    isNumber(raw) ||
    isBoolean(raw)
  ) {
    return raw;
  }
  return String(raw);
}

function styleValueOf(raw: DomPropertyValue): string | null {
  return raw === null || raw === undefined || raw === false
    ? null
    : String(raw);
}

/**
 * `onClick` and the native `onclick` both name the `click` event.
 *
 * The camel spelling is ours, so it also takes an action key; the native
 * spelling with a string is an inline HTML attribute, as it is in a browser.
 */
export function eventNameOf(name: string, value: PropInput): string | null {
  if (!/^on[A-Za-z]/.test(name)) return null;
  const ours = /^on[A-Z]/.test(name);
  if (!ours && !isFunction(value) && value !== null && value !== undefined)
    return null;
  return name.slice(2).toLowerCase();
}

/** Splits author props into static attributes, dynamic bindings, events, gate and key. */
export function applyProps(props: Props | null | undefined): AppliedProps {
  const out: AppliedProps = { attrs: {}, dyn: [], on: [] };
  if (!props) return out;
  for (const [name, value] of Object.entries(props)) {
    if (name === 'children') continue;
    if (name === 'can') {
      if (!isString(value))
        throw new InstructionError('can expects a gate string');
      out.gate = parseGate(value);
      continue;
    }
    if (name === 'key') {
      if (!isString(value)) throw new InstructionError('key expects a string');
      out.key = value;
      continue;
    }
    if (name === 'ref') {
      if (value === null || value === undefined) continue;
      if (!isFunction(value))
        throw new InstructionError('ref expects a function of the element');
      // SAFETY: a ref is called with the element and may return a cleanup; nothing else is read from it.
      out.ref = value as ElementRef;
      continue;
    }
    if (name.startsWith('on:')) {
      throw new InstructionError(
        `${name} is not an event; write on${name.slice(3, 4).toUpperCase()}${name.slice(4)}`,
      );
    }
    const event = eventNameOf(name, value);
    if (event !== null) {
      if (value === null || value === undefined) continue;
      if (isFunction(value)) {
        // SAFETY: under an `on*` name a function is the event's handler; only `ref` takes an element.
        out.on.push({ name: event, handler: value as EventHandler });
      } else if (isString(value)) out.on.push({ name: event, action: value });
      else
        throw new InstructionError(
          `${name} expects a handler or an action key`,
        );
      continue;
    }
    if (name.startsWith('class:')) {
      out.dyn.push({
        kind: 'class',
        name: name.slice(6),
        read: readerOf(value, Boolean),
      });
      continue;
    }
    if (name.startsWith('style:')) {
      out.dyn.push({
        kind: 'style',
        name: name.slice(6),
        read: readerOf(value, styleValueOf),
      });
      continue;
    }
    if (name.startsWith('.') || name.startsWith('prop:')) {
      const prop = name.startsWith('.') ? name.slice(1) : name.slice(5);
      out.dyn.push({
        kind: 'prop',
        name: prop,
        read: readerOf(value, (raw) => raw),
      });
      continue;
    }
    if (isDynamic(value)) {
      out.dyn.push({ kind: 'attr', name, read: readerOf(value, attrValueOf) });
      continue;
    }
    if (isFunction(value)) {
      throw new InstructionError(
        `Attribute ${name} received a function with parameters; use a zero-argument reader`,
      );
    }
    const str = attrText(name, value);
    if (str !== null) out.attrs[name] = str;
  }
  return out;
}

/**
 * A template's attribute slot: the text to write now, `null` for no
 * attribute, or a binding when the value can change.
 */
export function attrSlot(
  name: string,
  value: PropInput,
): DynAttr | string | null {
  if (isFunction(value) && value.length === 0) {
    // SAFETY: a zero-argument function in an attribute slot is its reader. It
    // is used as given: the attribute binding reads through and converts what
    // it yields, so nothing relies on it yielding an AttrValue already.
    return { kind: 'attr', name, read: value as Reader<AttrValue> };
  }
  if (isDynamic(value))
    return { kind: 'attr', name, read: readerOf(value, attrValueOf) };
  if (isFunction(value)) {
    throw new InstructionError(
      `Attribute ${name} received a function with parameters; use a zero-argument reader`,
    );
  }
  return attrText(name, value);
}

/** A template's event slot: a handler, an action key, or nothing. */
export function eventSlot(name: string, value: PropInput): EventBinding | null {
  if (value === null || value === undefined) return null;
  if (isFunction(value)) {
    // SAFETY: an event slot's function is the event's handler; only `ref` takes an element.
    return { name, handler: value as EventHandler };
  }

  if (isString(value)) return { name, action: value };
  throw new InstructionError(`on${name} expects a handler or an action key`);
}
